import { useEffect, useMemo, useState } from "react";
import { getVourcherDetail } from "api/Vourcher";
import { VoucherRule } from "api/VourcherType";

interface VoucherConditionModalProps {
    open: boolean;
    onClose: () => void;
    voucherId?: number | string | null;
}

const formatDate = (rawDate?: string | null): string => {
    if (!rawDate) return "Không giới hạn";
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return rawDate;
    return date.toLocaleDateString("vi-VN");
};

const formatRuleValue = (rule: VoucherRule): string => {
    if (rule.rule_description) return rule.rule_description;
    if (Array.isArray(rule.rule_value)) return `${rule.rule_type}: ${rule.rule_value.join(", ")}`;
    if (rule.rule_value === null || rule.rule_value === undefined || rule.rule_value === "") return `${rule.rule_type}`;
    return `${rule.rule_type}: ${rule.rule_value}`;
};

const getRuleStatusLabel = (isEligible?: boolean | null): string => {
    if (isEligible === true) return "Đã đạt";
    if (isEligible === false) return "Chưa đạt";
    return "Chưa xác định";
};

export default function VoucherConditionModal({
    open,
    onClose,
    voucherId,
}: VoucherConditionModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [productKeyword, setProductKeyword] = useState("");
    const [voucherDetail, setVoucherDetail] = useState<Awaited<ReturnType<typeof getVourcherDetail>>['data']>(null);

    useEffect(() => {
        if (!open || !voucherId) {
            setVoucherDetail(null);
            setError(null);
            setLoading(false);
            return;
        }

        let mounted = true;
        const loadVoucherDetail = async () => {
            setLoading(true);
            setError(null);
            const response = await getVourcherDetail(voucherId);
            if (!mounted) return;

            if (response.status && response.data) {
                setVoucherDetail(response.data);
            } else {
                setVoucherDetail(null);
                setError(response.msg || "Không tải được chi tiết mã giảm giá.");
            }
            setLoading(false);
        };

        loadVoucherDetail();
        return () => {
            mounted = false;
        };
    }, [open, voucherId]);

    const ruleList = useMemo(() => voucherDetail?.rules || [], [voucherDetail]);
    const eligibleRulesCount = useMemo(
        () => ruleList.filter((rule) => rule.is_eligible === true).length,
        [ruleList],
    );
    const progressPercent = useMemo(() => {
        if (!ruleList.length) return 0;
        return Math.round((eligibleRulesCount / ruleList.length) * 100);
    }, [eligibleRulesCount, ruleList.length]);
    const cartEligible = voucherDetail?.cart_eligible;
    const statusText = useMemo(() => {
        if (loading) return "Đang kiểm tra điều kiện áp dụng...";
        if (cartEligible === true) return "Bạn đã thỏa toàn bộ điều kiện của mã giảm giá";
        if (cartEligible === false) return `Bạn đang thỏa ${eligibleRulesCount}/${ruleList.length} điều kiện`;
        return "Đăng nhập để kiểm tra khả dụng theo giỏ hàng";
    }, [cartEligible, eligibleRulesCount, loading, ruleList.length]);
    const progressWidth = cartEligible === true ? 100 : progressPercent;
    const voucherProducts = useMemo(() => {
        const topLevelProducts = voucherDetail?.product_details || [];
        const ruleLevelProducts = ruleList.flatMap((rule) => rule.product_details || []);
        const mergedProducts = [...topLevelProducts, ...ruleLevelProducts];

        return mergedProducts.filter(
            (product, index, arr) => arr.findIndex((item) => item.id === product.id) === index,
        );
    }, [ruleList, voucherDetail]);
    const filteredVoucherProducts = useMemo(() => {
        const keyword = productKeyword.trim().toLowerCase();
        if (!keyword) return voucherProducts;

        return voucherProducts.filter((product) =>
            product.name?.toLowerCase().includes(keyword),
        );
    }, [productKeyword, voucherProducts]);
    if (!open) return null;

    return (
        <div
            role="presentation"
            className="MuiModal-root MuiDialog-root relative css-u14xag ctrl-p-0"
        >
            <div
                aria-hidden="true"
                className="MuiBackdrop-root css-919eu4"
                style={{
                    opacity: 1,
                    transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
            // onClick={onClose}
            />
            <div
                className="MuiDialog-container MuiDialog-scrollPaper css-ekeie0"
                role="presentation"
                style={{
                    opacity: 1,
                    transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onMouseDown={(e) => {
                    // chỉ đóng khi click vào nền container
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div
                    className="MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation24 MuiDialog-paper MuiDialog-paperScrollPaper MuiDialog-paperWidthSm css-uhb5lp"
                    role="dialog"
                    aria-labelledby=":r0:"
                >
                    {/* CLOSE */}
                    <div className="absolute top-[20px] right-0 z-10">
                        <div
                            className="flex gap-2 items-center !text-white cursor-pointer"
                            onClick={onClose}
                        >
                            <p className="text-[14px]">Đóng</p>
                            <svg
                                className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium !text-white css-vubbuv"
                                focusable="false"
                                aria-hidden="true"
                                viewBox="0 0 24 24"
                            >
                                <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </svg>
                        </div>
                    </div>

                    <div
                        data-root-voucher=""
                        className="w-[80vw] max-h-[1000px] h-[80vh] xl:w-[1028px] mx-auto px-0 space-y-4 font-[ggsm] overflow-x-hidden overflow-y-auto pb-3 bg-white w-full-767"
                        data-short-name="GIẢM 0.5%"
                        data-code="CHUYENKHOAN2602"
                        data-expired-date="2026-02-28T16:59:00Z"
                        data-action-status="INVALID"
                        data-usage-total="9985"
                    >
                        {/* HEADER */}
                        <div className="w-full rounded p-6 flex gap-6 bg-white ctrl-flex-col-991">
                            <div className="min-w-[360px] max-w-[40%] flex flex-col space-y-2">
                                <h3 className="text-[32px] font-bold line-clamp-2 text-black text-center">
                                    {voucherDetail?.name || "Chi tiết mã giảm giá"}
                                </h3>

                                {/* <div className="h-[100px] min-w-[100px] relative !my-6 mx-auto">
                                    <span
                                        style={{
                                            boxSizing: "border-box",
                                            display: "block",
                                            overflow: "hidden",
                                            width: "initial",
                                            height: "initial",
                                            background: "none",
                                            opacity: 1,
                                            border: "0px",
                                            margin: "0px",
                                            padding: "0px",
                                            position: "absolute",
                                            inset: "0px",
                                        }}
                                    >
                                        <img
                                            data-origin-url="https://storage.googleapis.com/thuocsi-live/images/2025c2634a8854f6fff28ec93e49311e"
                                            src="https://gcs.buymed.com/thuocsi-live/images/2025c2634a8854f6fff28ec93e49311e?quality=100"
                                            decoding="async"
                                            data-nimg="fill"
                                            style={{
                                                position: "absolute",
                                                inset: "0px",
                                                boxSizing: "border-box",
                                                padding: "0px",
                                                border: "none",
                                                margin: "auto",
                                                display: "block",
                                                width: "0px",
                                                height: "0px",
                                                minWidth: "100%",
                                                maxWidth: "100%",
                                                minHeight: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                                objectPosition: "left center",
                                            }}
                                            alt=""
                                        />
                                    </span>
                                </div> */}

                                <div className="flex items-center gap-2 flex-wrap !mt-2">
                                    <p className="text-[16px] font-medium min-w-fit">
                                        Mã sử dụng:
                                    </p>

                                    <div className="flex items-center gap-2 max-w-[90%]">
                                        <span
                                            className="text-[#1D39C4] text-[16px] font-medium cursor-pointer line-clamp-1"
                                            onClick={() => navigator.clipboard.writeText(voucherDetail?.code || "")}
                                        >
                                            {voucherDetail?.code || "---"}
                                        </span>

                                        <svg
                                            className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium text-[#0E1983] !text-[18px] cursor-pointer css-vubbuv"
                                            focusable="false"
                                            aria-hidden="true"
                                            viewBox="0 0 24 24"
                                            data-testid="FileCopyOutlinedIcon"
                                            onClick={() => navigator.clipboard.writeText(voucherDetail?.code || "")}
                                        >
                                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm-1 4H8c-1.1 0-1.99.9-1.99 2L6 21c0 1.1.89 2 1.99 2H19c1.1 0 2-.9 2-2V11l-6-6zM8 21V7h6v5h5v9H8z" />
                                        </svg>
                                    </div>
                                </div>

                                <p className="text-[#F53449] text-[16px] font-medium !mt-2">
                                    Hạn sử dụng: {formatDate(voucherDetail?.end_date)}
                                </p>

                                <div className="h-5 w-full rounded-[20px] bg-[#F0F0F0] flex items-center justify-center relative overflow-hidden !mt-2">
                                    <div
                                        className="bg-[#FFB951] absolute inset-0 z-1"
                                        style={{ width: "21%" }}
                                    />
                                    <p className="text-[10px] relative z-20">
                                        Đã dùng: 9985 lượt
                                    </p>
                                </div>
                            </div>

                            {/* RIGHT */}
                            <div className="flex flex-col flex-1 gap-4">
                                <div className="flex flex-col gap-2">
                                    <div className="flex justify-between items-center">
                                        <p className="text-[18px] !mb-0">
                                            <strong>Nội dung</strong>
                                        </p>

                                        {/* chua thoa dieu kien */}
                                        {/* <div className="h-5 w-[50%] max-w-[60%] flex-1 rounded-[20px] bg-[#F0F0F0] flex items-center justify-center relative overflow-hidden">
                                            <div
                                                className="absolute inset-0 z-1 transition-all bg-[#F0F0F0]"
                                                style={{ width: "0%" }}
                                            />
                                            <p className="text-[12px] relative z-20 line-clamp-1 px-2 text-black !mb-0">
                                                Chỉ còn vài điều kiện nữa, bạn thỏa được mã giảm giá
                                            </p>
                                        </div> */}

                                        <div className="h-5 w-[50%] max-w-[60%] flex-1 rounded-[20px] bg-[#F0F0F0] flex items-center justify-center relative overflow-hidden">
                                            <div
                                                className={`absolute inset-0 z-1 transition-all ${cartEligible === true ? "bg-gradient" : "bg-[#FFB951]"}`}
                                                style={{ width: `${progressWidth}%` }}
                                            />
                                            <p className={`text-[12px] relative z-20 line-clamp-1 px-2 ${cartEligible === true ? "text-white" : "text-black"}`}>
                                                {statusText}
                                            </p>
                                        </div>
                                    </div>

                                    <p className="font-[ggsr] text-[16px] !mb-0">
                                        {loading
                                            ? "Đang tải nội dung voucher..."
                                            : (error || ruleList[0]?.rule_description || "Không có mô tả chi tiết")}
                                    </p>
                                </div>

                                <details open className="group">
                                    <summary className="list-none [&::-webkit-details-marker]:hidden w-full flex items-center gap-2 justify-between cursor-pointer">
                                        <p className="text-[16px] font-semibold ">
                                            Điều kiện sử dụng
                                        </p>
                                        <svg
                                            className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium transition-all group-open:rotate-180 text-[var(--primary-color)] css-vubbuv"
                                            focusable="false"
                                            aria-hidden="true"
                                            viewBox="0 0 24 24"
                                            data-testid="KeyboardArrowDownIcon"
                                        >
                                            <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                                        </svg>
                                    </summary>

                                    <div className="flex flex-col gap-2 mt-2">
                                        <div className="flex gap-2">
                                            <div className="h-auto w-[1px] bg-[#3FB877] ml-2" />
                                            <div className="font-[ggsr] text-[16px] max-h-[200px] overflow-auto [&_a]:[text-decoration:revert] [&_a]:[color:revert] w-full">
                                                <ul className="list-disc space-y-2">
                                                    {loading && <li>Đang tải điều kiện sử dụng...</li>}
                                                    {!loading && error && <li>{error}</li>}
                                                    {!loading && !error && ruleList.length === 0 && (
                                                        <li>Không có điều kiện sử dụng.</li>
                                                    )}
                                                    {!loading && !error && ruleList.map((rule) => (
                                                        <li key={`${rule.id}-${rule.rule_type}`} className="list-item">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span>{formatRuleValue(rule)}</span>
                                                                {rule.target_names && rule.target_names.length > 0 && (
                                                                    <span className="text-[13px] text-[#6B7280]">
                                                                        ({rule.target_names.join(", ")})
                                                                    </span>
                                                                )}
                                                                <span
                                                                    className={`inline-flex items-center rounded-full px-2 py-[2px] text-[12px] ${rule.is_eligible === true
                                                                        ? "bg-[#E8FFF1] text-[#0E9F6E]"
                                                                        : rule.is_eligible === false
                                                                            ? "bg-[#FFF1F2] text-[#E11D48]"
                                                                            : "bg-[#F3F4F6] text-[#6B7280]"
                                                                        }`}
                                                                >
                                                                    {getRuleStatusLabel(rule.is_eligible)}
                                                                </span>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-2 w-full rounded-[10px] bg-[#F9FAFB] p-3 border border-[#E5E7EB]">
                                            <p className="text-[14px] text-[#374151] !mb-0">
                                                Đã đạt <strong>{eligibleRulesCount}</strong>/{ruleList.length} điều kiện
                                            </p>
                                            {voucherDetail?.cart_summary && (
                                                <p className="text-[13px] text-[#6B7280] !mb-0">
                                                    Giỏ hiện tại: {voucherDetail.cart_summary.total_items} sản phẩm, tổng tiền {Number(voucherDetail.cart_summary.total_amount).toLocaleString("vi-VN")}đ
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </details>

                                {/* <i className="text-red-500 mt-1">
                                    * Mua thêm 2 loại sản phẩm để sử dụng mã
                                </i> */}
                            </div>
                        </div>

                        <div
                            className="w-full py-[15px] px-5 flex justify-center rounded flex-col space-y-5 bg-white mx-2 !w-auto bg-gradient"
                        >
                            <div className="flex gap-3 items-center justify-center !mb-0">
                                <span
                                    style={{
                                        boxSizing: "border-box",
                                        display: "inline-block",
                                        overflow: "hidden",
                                        width: "initial",
                                        height: "initial",
                                        background: "none",
                                        opacity: 1,
                                        border: "0px",
                                        margin: "0px",
                                        padding: "0px",
                                        position: "relative",
                                        maxWidth: "100%",
                                    }}
                                >
                                    <span
                                        style={{
                                            boxSizing: "border-box",
                                            display: "block",
                                            width: "initial",
                                            height: "initial",
                                            background: "none",
                                            opacity: 1,
                                            border: "0px",
                                            margin: "0px",
                                            padding: "0px",
                                            maxWidth: "100%",
                                        }}
                                    >
                                        <img
                                            alt=""
                                            aria-hidden="true"
                                            src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2740%27%20height=%2740%27/%3e"
                                            style={{
                                                display: "block",
                                                maxWidth: "100%",
                                                width: "initial",
                                                height: "initial",
                                                background: "none",
                                                opacity: 1,
                                                border: "0px",
                                                margin: "0px",
                                                padding: "0px",
                                            }}
                                        />
                                    </span>

                                    <img
                                        src="https://thuocsi.vn/icons/icon-money-1.svg"
                                        decoding="async"
                                        data-nimg="intrinsic"
                                        style={{
                                            position: "absolute",
                                            inset: "0px",
                                            boxSizing: "border-box",
                                            padding: "0px",
                                            border: "none",
                                            margin: "auto",
                                            display: "block",
                                            width: "0px",
                                            height: "0px",
                                            minWidth: "100%",
                                            maxWidth: "100%",
                                            minHeight: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                </span>

                                <h2 className="text-[28px] font-medium text-white text-center !mb-0">
                                    Sản phẩm áp dụng khuyến mãi
                                </h2>
                            </div>

                            <div className="bg-white rounded-[8px] py-[10px] px-3 flex space-x-1">
                                <svg
                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-vubbuv"
                                    focusable="false"
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    data-testid="SearchIcon"
                                >
                                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                                </svg>

                                <input
                                    track-input-search=""
                                    refer-data-root-voucher="code"
                                    type="text"
                                    placeholder="Tìm kiếm sản phẩm"
                                    className="outline-none border-none flex flex-1"
                                    value={productKeyword}
                                    onChange={(e) => setProductKeyword(e.target.value)}
                                />
                            </div>
                        </div>

                        {loading && (
                            <div className="px-5 py-6 text-center text-[14px] text-[#6B7280]">
                                Đang tải danh sách sản phẩm...
                            </div>
                        )}

                        {!loading && filteredVoucherProducts.length === 0 && (
                            <div className="px-5 py-6 text-center text-[14px] text-[#6B7280]">
                                {voucherProducts.length === 0
                                    ? "Voucher này chưa có sản phẩm áp dụng."
                                    : "Không tìm thấy sản phẩm phù hợp."}
                            </div>
                        )}

                        {!loading && filteredVoucherProducts.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 bg-[#f5f5f5] p-5">
                                {filteredVoucherProducts.map((product) => {
                                    const productSlug = product.detail?.slug || product.slug;
                                    const productHref = productSlug ? `/product/${productSlug}` : "#";
                                    const displayPrice = Number(product.discount_price || product.price || 0);
                                    const originalPrice = Number(product.price || 0);

                                    return (
                                        <a
                                            key={product.id}
                                            href={productHref}
                                            className="block bg-white rounded-[10px] border border-[#E5E7EB] p-3 transition-all hover:shadow-md"
                                        >
                                            <div className="w-full aspect-square bg-[#F9FAFB] rounded-[8px] overflow-hidden flex items-center justify-center">
                                                {product.avatar ? (
                                                    <img
                                                        src={product.avatar}
                                                        alt={product.name}
                                                        className="w-full h-full object-contain"
                                                    />
                                                ) : (
                                                    <span className="text-[12px] text-[#9CA3AF]">Không có ảnh</span>
                                                )}
                                            </div>

                                            <p className="mt-3 mb-1 text-[14px] text-[#111827] font-medium line-clamp-2 min-h-[42px]">
                                                {product.name}
                                            </p>

                                            <div className="flex flex-col gap-1">
                                                <p className="text-[14px] font-semibold text-[#E11D48] !mb-0">
                                                    {displayPrice.toLocaleString("vi-VN")}đ
                                                </p>

                                                {originalPrice > displayPrice && (
                                                    <p className="text-[12px] text-[#9CA3AF] line-through !mb-0">
                                                        {originalPrice.toLocaleString("vi-VN")}đ
                                                    </p>
                                                )}

                                                {typeof product.percent === "number" && product.percent > 0 && (
                                                    <p className="text-[12px] text-[#0E9F6E] !mb-0">
                                                        Giảm {product.percent}%
                                                    </p>
                                                )}
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                        )}

                        {/* <div className="styles_pagiContainer__B0imj MuiBox-root css-1vwfmvr">
                            <nav
                                aria-label="pagination navigation"
                                className="MuiPagination-root jss99"
                            >
                                <ul className="MuiPagination-ul">
                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page Mui-disabled Mui-disabled"
                                            tabIndex={-1}
                                            type="button"
                                            disabled
                                            aria-label="Go to previous page"
                                        >
                                            <svg
                                                className="MuiSvgIcon-root MuiPaginationItem-icon"
                                                focusable="false"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                            </svg>
                                        </button>
                                    </li>

                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page Mui-selected"
                                            tabIndex={0}
                                            type="button"
                                            aria-current="true"
                                            aria-label="page 1"
                                        >
                                            1
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>

                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page"
                                            tabIndex={0}
                                            type="button"
                                            aria-label="Go to page 2"
                                        >
                                            2
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>

                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page"
                                            tabIndex={0}
                                            type="button"
                                            aria-label="Go to page 3"
                                        >
                                            3
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>

                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page"
                                            tabIndex={0}
                                            type="button"
                                            aria-label="Go to page 4"
                                        >
                                            4
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>

                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page"
                                            tabIndex={0}
                                            type="button"
                                            aria-label="Go to page 5"
                                        >
                                            5
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>

                                    <li>
                                        <div className="MuiPaginationItem-root MuiPaginationItem-ellipsis">
                                            …
                                        </div>
                                    </li>

                                    <li />

                                    <li>
                                        <button
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page"
                                            tabIndex={0}
                                            type="button"
                                            aria-label="Go to next page"
                                        >
                                            <svg
                                                className="MuiSvgIcon-root MuiPaginationItem-icon"
                                                focusable="false"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                            </svg>
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
}
